import React, { useState, useEffect, useRef } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getSortedRowModel,
  ColumnDef,
  FilterFn,
  SortingState,
} from '@tanstack/react-table';
import { Download, Search, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import type { Entry, Pagination } from '../types/data';
import { Document, Page } from 'react-pdf';
import '../utils/pdfConfig';

const columnHelper = createColumnHelper<Entry>();

interface DataTableProps {
  data: Entry[];
  allFilteredData: Entry[];
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onRecordsPerPageChange: (recordsPerPage: number) => void;
  onSearch: (search: string) => void;
  onSortChange: (sortBy: 'created_date_time_asc' | 'created_date_time_desc' | 'model') => void;
  isLoading: boolean;
  setIsEditModalOpen: (val :boolean) => void;
  setUserData: (entry: Entry | null) => void;
}

const globalFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
  const search = filterValue.toLowerCase();
  return Object.values(row.original)
    .join(' ')
    .toLowerCase()
    .includes(search);
};

export default function DataTable({ data, allFilteredData, pagination, onPageChange, onRecordsPerPageChange, onSearch, onSortChange, isLoading, setIsEditModalOpen, setUserData }: DataTableProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null); // Keep for backward compatibility
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState<number | null>(null);
  const [pdfCurrentPage, setPdfCurrentPage] = useState<number>(1);
  const [pdfLoadError, setPdfLoadError] = useState<boolean>(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [sortBy, setSortBy] = useState<'created_date_time_asc' | 'created_date_time_desc' | 'model'>('created_date_time_desc');

  function viewPDF(url: string) {
    // Navigate to the PDF viewer page with the PDF URL as a query parameter
    navigate(`/pdf-viewer?url=${encodeURIComponent(url)}`);
  }
  
  function viewImage(url: string) {
    // Navigate to the image viewer page with the image URL as a query parameter
    navigate(`/image-viewer?url=${encodeURIComponent(url)}`);
  }
  
  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setPdfNumPages(numPages);
    setPdfLoadError(false);
  }
  
  function onDocumentLoadError(error: Error) {
    console.error('Error loading PDF:', error);
    setPdfLoadError(true);
  }

  const columns = [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('full_name', {
      header: 'Name',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('contact_no', {
      header: 'Contact',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('state', {
      header: 'State',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('serial_number', {
      header: 'Serial Number',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('invoice_no', {
      header: 'Invoice',
      cell: info => (
        <div className="flex items-center gap-2">
          <span>{info.getValue()}</span>
          {info.row.original.invoice_no_img && (
            <button
              type="button"
              onClick={(event) => {event.stopPropagation();  (info.row.original.invoice_no_img.endsWith(".pdf")) ? viewPDF(info.row.original.invoice_no_img) : viewImage(info.row.original.invoice_no_img)}}
              className="text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('status', {
    header: 'Status',
    cell: info => (
      <span
        className={`px-2 py-1 rounded-full text-xs ${
          info.getValue() === 'pending'
            ? 'bg-yellow-100 text-yellow-800'
            : info.getValue() === 'redeemed'
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}
      >
        {info.getValue()}
      </span>
    ),
    filterFn: 'equals' as const,
  }),
  columnHelper.accessor('reject_reason', {
    header: 'Rejection Reason',
    cell: info => info.getValue(),
    filterFn: 'equals' as const,
  }),
  // columnHelper.accessor('category', {
  //   header: 'Category',
  //   cell: info => info.getValue(),
  //   filterFn: 'equals' as const,
  // }),
  columnHelper.accessor('model', {
    header: 'Model',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('purchased_value', {
    header: 'Value',
    cell: info => `RM ${info.getValue()}`,
  }),
  columnHelper.accessor('purchased_date', {
    header: 'Purchase Date',
    cell: info => new Date(info.getValue()).toLocaleDateString('en-GB'),
  }),
  columnHelper.accessor('home_office_usage', {
    header: 'Home Office Usage',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('createdAt', {
    header: 'Submission Date & Time',
    cell: info => new Date(info.getValue()).toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
  }),
];



  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchTerm);
  };

  const handleSortByChange = (value: 'created_date_time_asc' | 'created_date_time_desc' | 'model') => {
    setSortBy(value);
    if (value === 'created_date_time_asc') {
      setSorting([{ id: 'createdAt', desc: false }]);
    } else if (value === 'created_date_time_desc') {
      setSorting([{ id: 'createdAt', desc: true }]);
    } else {
      setSorting([{ id: 'model', desc: false }]);
    }
    onSortChange(value);
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    enableSorting: false,
  });

  const exportToCSV = () => {
    // Get the base URL of the current application
    const baseUrl = window.location.origin;
    
    const standardHeaders = columns.map(col => (col as ColumnDef<Entry, any>).header as string);
    const headers = [...standardHeaders, 'Invoice URL'];
    
    const columnIds = columns.map(col => (col as any).accessorKey as string);

    const rows = allFilteredData.map(entry => {
      const standardCells = columnIds.map(id => {
        let value = (entry as any)[id];
        if (id === 'purchased_date') {
          value = new Date(value).toLocaleDateString('en-GB');
        } else if (id === 'createdAt') {
          value = new Date(value).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          });
        } else if (id === 'purchased_value') {
          value = `RM ${value}`;
        }
        return typeof value === 'string' ? `"${value}"` : value;
      });
      
      // Get the original S3 URL
      const originalUrl = entry.invoice_no_img || '';
      
      // Create a masked URL that points to our image viewer for images or PDF viewer for PDFs
      let maskedUrl = '';
      if (originalUrl) {
        if (originalUrl.endsWith('.pdf')) {
          // For PDFs, create a URL to our PDF viewer
          maskedUrl = `${baseUrl}/pdf-viewer?url=${encodeURIComponent(originalUrl)}`;
        } else {
          // For images, create a URL to our image viewer
          maskedUrl = `${baseUrl}/image-viewer?url=${encodeURIComponent(originalUrl)}`;
        }
      }
      
      return [...standardCells, `"${maskedUrl}"`];
    });
    
    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `epson-rewards-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uniqueStatuses = Array.from(new Set(data.map(item => item.status)));
  const uniqueCategories = Array.from(new Set(data.map(item => item.category)));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search all columns..."
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors duration-200 w-full sm:w-64"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            disabled={isLoading}
          >
            Search
          </button>
        </form>
        <div className="flex gap-4 w-full sm:w-auto items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="sortBy" className="text-sm text-gray-600 dark:text-gray-300">Sort by:</label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => handleSortByChange(e.target.value as 'created_date_time_asc' | 'created_date_time_desc' | 'model')}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors duration-200"
            >
              <option value="created_date_time_asc">Submission Date (Latest to Earliest)</option>
              <option value="created_date_time_desc">Submission Date (Earliest to Latest)</option>
              <option value="model">Model</option>
            </select>
          </div>
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>
      
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 transition-colors duration-200">
        <table className="min-w-full bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800 transition-colors duration-200">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700 transition-colors duration-200">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400 transition-colors duration-200">
                  Loading...
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400 transition-colors duration-200">
                  No data available
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => {return (
                <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 cursor-pointer" onClick={() => {if (window.getSelection()?.toString()) {return;} setIsEditModalOpen(true); setUserData(row.original)}}>
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 transition-colors duration-200"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              )})
            )}
          </tbody>
        </table>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500 dark:text-gray-300">
            Show
            <select
              value={pagination.records_per_page}
              onChange={(e) => onRecordsPerPageChange(Number(e.target.value))}
              className="mx-2 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors duration-200"
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value={pagination.total_records}>All</option>
            </select>
            entries
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-300">
            Showing {pagination.records_per_page * (pagination.current_page - 1) + 1} to{' '}
            {Math.min(pagination.records_per_page * pagination.current_page, parseInt(pagination.total_records))} of{' '}
            {pagination.total_records} entries
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(1)}
            disabled={pagination.current_page === 1 || isLoading}
            className="flex items-center gap-1 px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
            title="First Page"
          >
            <ChevronLeft className="h-4 w-4 text-gray-700 dark:text-gray-200" />
            <ChevronLeft className="h-4 w-4 -ml-3 text-gray-700 dark:text-gray-200" />
            <span className="text-sm text-gray-700 dark:text-gray-200">First</span>
          </button>
          <button
            onClick={() => onPageChange(pagination.current_page - 1)}
            disabled={pagination.current_page === 1 || isLoading}
            className="flex items-center gap-1 px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
            title="Previous Page"
          >
            <ChevronLeft className="h-4 w-4 text-gray-700 dark:text-gray-200" />
            <span className="text-sm text-gray-700 dark:text-gray-200">Previous</span>
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-300 mx-4">
            Page {pagination.current_page} of {pagination.total_pages}
          </span>
          <button
            onClick={() => onPageChange(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.total_pages || isLoading}
            className="flex items-center gap-1 px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
            title="Next Page"
          >
            <span className="text-sm text-gray-700 dark:text-gray-200">Next</span>
            <ChevronRight className="h-4 w-4 text-gray-700 dark:text-gray-200" />
          </button>
          <button
            onClick={() => onPageChange(pagination.total_pages)}
            disabled={pagination.current_page === pagination.total_pages || isLoading}
            className="flex items-center gap-1 px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
            title="Last Page"
          >
            <span className="text-sm text-gray-700 dark:text-gray-200">Last</span>
            <ChevronRight className="h-4 w-4 text-gray-700 dark:text-gray-200" />
            <ChevronRight className="h-4 w-4 -ml-3 text-gray-700 dark:text-gray-200" />
          </button>
        </div>
      </div>

      {/* Image Modal */}
      <Modal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
      >
        <div className="flex items-center justify-center p-4">
          <img
            src={selectedImage || ''}
            alt="Invoice"
            className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-lg"
          />
        </div>
      </Modal>

      {/* PDF Modal */}
      <Modal
        isOpen={!!selectedPdf}
        onClose={() => {
          setSelectedPdf(null);
          setPdfLoadError(false);
        }}
      >
        <div className="flex flex-col items-center justify-center p-4 h-[80vh] w-full overflow-auto">
          {pdfLoadError ? (
            <div className="flex flex-col items-center justify-center space-y-4">
              <p className="text-red-500 dark:text-red-400">Failed to load PDF file due to security restrictions.</p>
              <div className="flex space-x-4">
                <a 
                  href={selectedPdf || ''} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
                >
                  Open in New Tab
                </a>
                <button
                  onClick={() => window.open(selectedPdf || '', '_blank')}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  Download PDF
                </button>
              </div>
            </div>
          ) : (
            <>
              <Document
                file={selectedPdf || ''}
                onLoadError={onDocumentLoadError}
                onLoadSuccess={onDocumentLoadSuccess}
                className="w-full h-full rounded-lg shadow-lg"
              >
                <Page 
                  pageNumber={pdfCurrentPage} 
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  width={Math.min(window.innerWidth * 0.8, 800)}
                />
              </Document>
              
              {pdfNumPages && pdfNumPages > 1 && (
                <div className="flex items-center justify-center mt-4 space-x-4">
                  <button 
                    onClick={() => setPdfCurrentPage(page => Math.max(page - 1, 1))}
                    disabled={pdfCurrentPage <= 1}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded-md disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm">
                    Page {pdfCurrentPage} of {pdfNumPages}
                  </span>
                  <button 
                    onClick={() => setPdfCurrentPage(page => Math.min(page + 1, pdfNumPages || 1))}
                    disabled={pdfCurrentPage >= (pdfNumPages || 1)}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded-md disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}